/**
 * Self-Service API Service
 * Provides methods for users to manage their own accounts (password change, sign-in activity).
 *
 * Uses Bearer token authentication - all requests require authenticated user context.
 */

import { ApiService } from './api';
import type {
  PasswordChangeRequest,
  PasswordChangeResponse,
  AuthenticationMethodsResponse,
  EmailMethodResponse,
  PhoneMethodResponse,
} from '../types/selfService';

/**
 * Self-Service API Client
 * Methods for user self-service operations
 */
class SelfServiceApiServiceClass {
  /**
   * Get base URL for self-service endpoints.
   */
  private getBaseUrl(): string {
    return '/api/self-service';
  }

  /**
   * Change user's own password.
   *
   * Requires current password for validation. User can only change their own password.
   *
   * @param request - Password change request with current and new passwords
   * @returns Success response
   * @throws Error on 400 (invalid current password) or 500 (server error)
   */
  async changePassword(
    request: PasswordChangeRequest
  ): Promise<PasswordChangeResponse> {
    const endpoint = `${this.getBaseUrl()}/password`;

    // Convert camelCase to snake_case for backend API
    const backendRequest = {
      current_password: request.currentPassword,
      new_password: request.newPassword,
    };

    const response = await ApiService.request<PasswordChangeResponse>(endpoint, {
      method: 'POST',
      body: JSON.stringify(backendRequest),
    });

    return response;
  }

  /**
   * List user's MFA methods (authentication methods).
   *
   * Returns all authentication methods registered for the authenticated user.
   * User can only view their own methods.
   *
   * @returns List of authentication methods
   * @throws Error on 401 (not authenticated) or 500 (server error)
   */
  async listMFAMethods(): Promise<AuthenticationMethodsResponse> {
    const endpoint = `${this.getBaseUrl()}/mfa-methods`;

    // Backend returns snake_case fields, need to transform to camelCase
    const backendResponse = await ApiService.request<{
      methods: Array<{
        id: string;
        type: string;
        display_name: string | null;
        phone_number: string | null;
        email: string | null;
        created_at: string | null;
      }>;
    }>(endpoint, {
      method: 'GET',
    });

    // Transform snake_case to camelCase for frontend
    const response: AuthenticationMethodsResponse = {
      methods: backendResponse.methods.map((method) => ({
        id: method.id,
        type: method.type,
        displayName: method.display_name,
        phoneNumber: method.phone_number,
        email: method.email,
        createdAt: method.created_at,
      })),
    };

    return response;
  }

  /**
   * Remove an MFA method.
   *
   * Deletes the specified authentication method. User can only remove their own methods.
   * Cannot delete the last remaining authentication method.
   *
   * @param methodId - Authentication method ID to delete
   * @param methodType - Optional method type (e.g., 'phoneAuthenticationMethod').
   *                    When provided, avoids GET request to determine type.
   *                    Recommended for newly created methods to avoid eventual consistency issues.
   * @returns Success response
   * @throws Error on 404 (method not found), 400 (last remaining method), or 500 (server error)
   */
  async removeMFAMethod(methodId: string, methodType?: string): Promise<PasswordChangeResponse> {
    let endpoint = `${this.getBaseUrl()}/mfa-methods/${methodId}`;

    // Add method_type as query parameter if provided
    if (methodType) {
      endpoint += `?method_type=${encodeURIComponent(methodType)}`;
    }

    const response = await ApiService.request<PasswordChangeResponse>(endpoint, {
      method: 'DELETE',
    });

    return response;
  }

  /**
   * Add email authentication method (admin-assisted).
   *
   * Uses backend admin permissions to add email authentication method on behalf of
   * authenticated user. User can only add to their own account.
   *
   * @param emailAddress - Email address to use for authentication
   * @returns Success response with method ID
   * @throws Error on 400 (email already exists), 422 (validation error), or 500 (server error)
   */
  async addEmailMethod(emailAddress: string): Promise<EmailMethodResponse> {
    const endpoint = `${this.getBaseUrl()}/mfa-methods/email`;

    // Convert camelCase to snake_case for backend API
    const backendRequest = {
      email_address: emailAddress,
    };

    // Backend returns snake_case fields, need to transform to camelCase
    const backendResponse = await ApiService.request<{
      success: boolean;
      message: string;
      method_id: string | null;
    }>(endpoint, {
      method: 'POST',
      body: JSON.stringify(backendRequest),
    });

    // Transform snake_case to camelCase for frontend
    const response: EmailMethodResponse = {
      success: backendResponse.success,
      message: backendResponse.message,
      methodId: backendResponse.method_id,
    };

    return response;
  }

  /**
   * Add phone authentication method (admin-assisted).
   *
   * Uses backend admin permissions to add phone authentication method on behalf of
   * authenticated user. User can only add to their own account.
   *
   * @param phoneNumber - Phone number in E.164 format (e.g., +14255551234)
   * @param phoneType - Phone type (mobile, alternateMobile, office)
   * @returns Success response with method ID
   * @throws Error on 400 (phone already exists), 422 (validation error), or 500 (server error)
   */
  async addPhoneMethod(phoneNumber: string, phoneType: string = 'mobile'): Promise<PhoneMethodResponse> {
    const endpoint = `${this.getBaseUrl()}/mfa-methods/phone`;

    // Convert camelCase to snake_case for backend API
    const backendRequest = {
      phone_number: phoneNumber,
      phone_type: phoneType,
    };

    // Backend returns snake_case fields, need to transform to camelCase
    const backendResponse = await ApiService.request<{
      success: boolean;
      message: string;
      method_id: string | null;
    }>(endpoint, {
      method: 'POST',
      body: JSON.stringify(backendRequest),
    });

    // Transform snake_case to camelCase for frontend
    const response: PhoneMethodResponse = {
      success: backendResponse.success,
      message: backendResponse.message,
      methodId: backendResponse.method_id,
    };

    return response;
  }

}

// Export singleton instance
export const SelfServiceApiService = new SelfServiceApiServiceClass();
